import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete,
  IconButton,
  Collapse,
  Snackbar,
  Fade,
  Divider,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon, FileUpload as FileUploadIcon, Close as CloseIcon, Assignment as ConsignmentIcon, ChecklistRtl as ChecklistIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { isAxiosError } from 'axios';
import { Brand, Model, Year } from '../../services/fipeService';
import fipeService from '../../services/fipeService';
import vehicleService from '../../services/vehicleService';
import { ConsignmentCommissionType, ConsignmentExposure, CONSIGNMENT_COMMISSION_TYPE_LABELS, CONSIGNMENT_EXPOSURE_LABELS } from '../../types';
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import VehicleChecklistDialog from './VehicleChecklistDialog';
import { prepareVehicleImageForUpload } from '../../utils/imageCompression';
import { generateVehicleDescription, VEHICLE_DESCRIPTION_VARIANTS } from '../../utils/vehicleDescription';
import VehicleImageSorter from './VehicleImageSorter';

/** Carro usa tabela FIPE; moto/outro = cadastro manual (sem consulta FIPE). */
type VehicleCategory = 'car' | 'motorcycle' | 'other';

interface VehicleFormData {
  brand_id: number;
  model_id: number;
  year_id: string;
  color: string;
  plate: string;
  /** Se true, permite salvar sem placa (ex.: veículo 0 km). */
  no_plate: boolean;
  /** Para outros veículos (jet/quadriciclo): identificador no lugar de placa. */
  identification: string;
  /** Para outros veículos: permite salvar sem identificação (ex.: 0 horas). */
  no_identification: boolean;
  chassis: string;
  purchase_price: number;
  sale_price_promo: number;
  sale_price: number;
  purchase_date: string;
  notes: string;
  internal_notes: string;
  status: 'available' | 'sold' | 'maintenance';
  codigo_fipe: string;
  vehicleType: string;
  codigo_ano: string;
  images: any[];
  km: string;
  /** Para outros veículos: horas de uso no lugar de km. */
  hours: string;
  /** Para outros veículos: zero horas. */
  zero_hours: boolean;
  cambio: string;
  motor: string;
  // Consignação
  is_consignado: boolean;
  consignment_commission_type: ConsignmentCommissionType;
  consignment_commission: number;
  consignment_exposure: ConsignmentExposure | '';
  consignment_owner_name: string;
  consignment_owner_contact: string;
  consignment_contract_date: string;
  consignment_notes: string;
  consignment_zero_commission_reason: string;

  // Vitrine pública (controle por cadastro)
  showcase_top: boolean;
  showcase_featured: boolean;
  showcase_week_highlight: boolean;
  showcase_priority: number;
}

function toApiConsignmentCommissionType(t: ConsignmentCommissionType): 'percent' | 'value' {
  return t === 'fixed' ? 'value' : 'percent';
}

function fromApiConsignmentCommissionType(t: unknown): ConsignmentCommissionType {
  if (t === 'value' || t === 'fixed') return 'fixed';
  return 'percent';
}

const MAX_VEHICLE_IMAGES = 30;

const COMMON_COLORS = ['Branco', 'Preto', 'Prata', 'Cinza', 'Vermelho', 'Azul', 'Bege', 'Marrom', 'Verde', 'Amarelo', 'Laranja', 'Dourado', 'Vinho'];

const isImageFile = (file: File) => file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name);

const imageOrderKey = (images: any[]) => images.map((image) => image.url).join('|');

const defaultFormData: VehicleFormData = {
  brand_id: 0,
  model_id: 0,
  year_id: '',
  color: '',
  plate: '',
  no_plate: false,
  identification: '',
  no_identification: false,
  chassis: '',
  purchase_price: 0,
  sale_price_promo: 0,
  sale_price: 0,
  purchase_date: new Date().toISOString().split('T')[0],
  notes: '',
  internal_notes: '',
  status: 'available',
  codigo_fipe: '',
  vehicleType: '',
  codigo_ano: '',
  images: [],
  km: '',
  hours: '',
  zero_hours: false,
  cambio: '',
  motor: '',
  is_consignado: false,
  consignment_commission_type: 'percent',
  consignment_commission: 0,
  consignment_exposure: '',
  consignment_owner_name: '',
  consignment_owner_contact: '',
  consignment_contract_date: '',
  consignment_notes: '',
  consignment_zero_commission_reason: '',

  showcase_top: false,
  showcase_featured: false,
  showcase_week_highlight: false,
  showcase_priority: 0,
};

/** Mensagem amigável para falhas de upload (rede, 422, HEIC no navegador, etc.). */
function formatImageUploadError(err: unknown): string {
  const mapLaravelUploadKey = (key: string): string | null => {
    if (key === 'validation.uploaded') {
      return 'O servidor não recebeu a foto. No iPhone, aguarde alguns segundos (a imagem é otimizada antes do envio) e tente de novo. Se persistir, use Ajustes → Câmera → Formatos → "Mais compatível".';
    }
    return null;
  };

  if (err && typeof err === 'object') {
    const o = err as Record<string, unknown>;
    const msg = typeof o.message === 'string' ? o.message : '';
    if (msg && (msg.includes('ERR_LIBHEIF') || msg.toLowerCase().includes('heif') || msg.toLowerCase().includes('libheif'))) {
      return 'Este formato HEIC não pôde ser processado aqui. Envie em JPG ou PNG, ou no iPhone use Fotos → Transferir para Mac ou PC → “Automático” (JPEG).';
    }
    if (o.code === 2 && typeof msg === 'string' && msg.length > 0) {
      return 'Formato de imagem não suportado neste navegador. Tente JPG ou PNG.';
    }
  }
  if (isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data as Record<string, unknown> | string | undefined;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      const fieldErrors = data.errors;
      if (fieldErrors && typeof fieldErrors === 'object' && !Array.isArray(fieldErrors)) {
        const arquivo = (fieldErrors as Record<string, unknown>).arquivo;
        if (Array.isArray(arquivo) && arquivo.length > 0) {
          const first = arquivo[0];
          if (typeof first === 'string') {
            const mapped = mapLaravelUploadKey(first);
            if (mapped) return mapped;
          }
        }
      }
      const m = data.message;
      if (typeof m === 'string' && m.trim()) {
        const mapped = mapLaravelUploadKey(m.trim());
        if (mapped) return mapped;
        return m;
      }
      const errs = data.errors;
      if (Array.isArray(errs) && errs.every((x) => typeof x === 'string')) {
        return errs.join(' ');
      }
    }
    if (status === 422) return 'O servidor não aceitou este arquivo. Confira formato e tamanho da imagem.';
    if (status === 413) return 'O arquivo é grande demais. Reduza o tamanho ou a resolução da foto.';
    if (status != null && status >= 500) return 'Serviço indisponível no momento. Tente de novo em alguns instantes.';
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message && err.message !== '[object Object]') {
    return err.message;
  }
  return 'Não foi possível enviar a imagem. Verifique sua conexão e tente outro arquivo.';
}

const VehicleForm: React.FC = () => {
  const { id } = useParams();  // Pega o id da URL

  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<VehicleFormData>(defaultFormData);
  const [initialFormData, setInitialFormData] = useState<VehicleFormData | null>(null); /*Usado para edicao*/
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [draggingFiles, setDraggingFiles] = useState(false);
  const [autoNotes, setAutoNotes] = useState(!id);
  const [notesVariant, setNotesVariant] = useState(0);
  const [uploadSnackbar, setUploadSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  // Estados para FIPE API
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [years, setYears] = useState<Year[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);
  const [fipePrice, setFipePrice] = useState<number | null>(null);
  const [fipePriceLoading, setFipePriceLoading] = useState(false);
  const [existingDocs, setExistingDocs] = useState<any[]>([])
  const [loadingDoc, setLoadingDoc] = useState(false);
const isDisabled = loadingDoc || saving; // desabilita tudo enquanto estiver carregando ou salvando
  // Para os componentes Autocomplete
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [selectedYear, setSelectedYear] = useState<Year | null>(null);

  const isEditing = !!id;
  const [vehicleCategory, setVehicleCategory] = useState<VehicleCategory | null>(isEditing ? null : 'car');
  const useFipeTable = vehicleCategory === 'car';
  const [currentIndex, setCurrentIndex] = useState(0);
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);

  const checklistMeta = useMemo(() => {
    const plateOrId =
      vehicleCategory === 'other'
        ? formData.no_identification
          ? 'Sem identificação'
          : formData.identification || ''
        : formData.no_plate
          ? 'Sem placa'
          : formData.plate;

    let kmDisplay = '';
    if (vehicleCategory === 'other') {
      kmDisplay =
        formData.zero_hours || formData.no_identification
          ? '0 horas (ou sem marcação)'
          : formData.hours
            ? `${formData.hours} h`
            : '';
    } else {
      kmDisplay = formData.km || '';
    }

    const yearStr = useFipeTable ? selectedYear?.name?.toString() || formData.year_id || '' : formData.year_id || '';

    return {
      brand: selectedBrand?.name?.trim() || '—',
      model: selectedModel?.name?.trim() || '—',
      year: yearStr,
      color: formData.color || '',
      plateOrId: plateOrId || '—',
      km: kmDisplay || '—',
      chassis: formData.chassis || '',
      isConsignado: formData.is_consignado,
      consignmentOwner: formData.consignment_owner_name?.trim() || undefined,
      consignmentContact: formData.consignment_owner_contact?.trim() || undefined,
      vehicleIdLabel: id,
    };
  }, [
    vehicleCategory,
    formData.no_identification,
    formData.identification,
    formData.no_plate,
    formData.plate,
    formData.zero_hours,
    formData.hours,
    formData.km,
    formData.chassis,
    formData.color,
    formData.year_id,
    formData.is_consignado,
    formData.consignment_owner_name,
    formData.consignment_owner_contact,
    useFipeTable,
    selectedBrand,
    selectedModel,
    selectedYear,
    id,
  ]);

  const nextImage = () => {
    if (currentIndex < formData.images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const prevImage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const [documents, setDocuments] = useState<{
    url: unknown; file: File, type: string
  }[]>([])

  const showUploadError = (message: string) => {
    setUploadSnackbar({ open: true, message });
  };

  const closeUploadSnackbar = () => {
    setUploadSnackbar((s) => ({ ...s, open: false }));
  };

  const handleAddDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    const input = e.target;
    const type = prompt('Tipo do documento?') || 'outro';
    setLoadingDoc(true);

    try {
      const url = await uploadImage(file);
      setDocuments((prev) => [...prev, { file, type, url }]);
    } catch (err) {
      console.error('Erro ao enviar documento:', err);
      showUploadError(formatImageUploadError(err));
    } finally {
      setLoadingDoc(false);
      input.value = '';
    }
  };


  const removeExistingDoc = async (iddoc: number) => {
    if (!iddoc && id) {
      alert('Erro ao apagar.')
      return
    }

    const confirmDelete = window.confirm('Deseja apagar a documento do banco de dados?')
    if (!confirmDelete) return

    try {
      const result = await vehicleService.deleteDocVehicle(parseInt(id!), iddoc)

      if (result.status === 204) {
          setExistingDocs(prev => prev.filter(d => d.id !== iddoc));

      }
    } catch (error) {
      alert('Erro ao apagar.')
    }
  }

  const handleRemoveDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index))
  }


  const currentImage = formData.images[currentIndex];

  useEffect(() => {
    if (vehicleCategory !== 'car') return;
    const loadBrands = async () => {
      try {
        setLoadingBrands(true);
        const brandsData = await fipeService.getBrands();
        setBrands(brandsData);
        setLoadingBrands(false);
      } catch (error) {
        console.error('Erro ao carregar marcas de veículos:', error);
        setError('Erro ao carregar marcas de veículos.');
        setLoadingBrands(false);
      }
    };
    loadBrands();
  }, [vehicleCategory]);

  useEffect(() => {
    const loadVehicleData = async () => {
      if (isEditing) {
        setLoading(true);

        try {
          // Carregar dados do veículo
          const vehicleData = await vehicleService.getVehicle(parseInt(id!));

          const hasFipeCode = !!(vehicleData.codigo_fipe && String(vehicleData.codigo_fipe).trim());
          const vtLower = String(vehicleData.vehicleType || '').toLowerCase();
          let cat: VehicleCategory = 'car';
          if (!hasFipeCode) {
            cat = vtLower.includes('moto') ? 'motorcycle' : 'other';
          }
          setVehicleCategory(cat);

          const dbValueBrand = { name: vehicleData.brand, id: 1 };
          const dbValueModel = { name: vehicleData.model, id: 1 };
          const dbValueYeah = { name: vehicleData.codigo_ano, id: '1' };
          setSelectedBrand(dbValueBrand);
          setSelectedModel(dbValueModel);
          setSelectedYear(dbValueYeah);

          const images = vehicleData.images || [];

          //** sentando brand e model pra 1 para nao mandar preencher... */
          const consignmentData = {
            is_consignado: !!vehicleData.is_consignado,
            consignment_commission_type: fromApiConsignmentCommissionType((vehicleData as any).consignment_commission_type) || 'percent',
            consignment_commission: vehicleData.consignment_commission ?? 0,
            consignment_exposure: (vehicleData.consignment_exposure as ConsignmentExposure) || '',
            consignment_owner_name: vehicleData.consignment_owner_name || '',
            consignment_owner_contact: vehicleData.consignment_owner_contact || '',
            consignment_contract_date: vehicleData.consignment_contract_date || '',
            consignment_notes: vehicleData.consignment_notes || '',
            consignment_zero_commission_reason: (vehicleData as any).consignment_zero_commission_reason || '',
          };

          setFormData({
            brand_id: 1,
            model_id: 1,
            year_id: vehicleData.year.toString(),
            color: vehicleData.color,
            plate: vehicleData.plate || '',
            no_plate: !!(vehicleData.no_plate || !vehicleData.plate),
            identification: (vehicleData as any).identification || '',
            no_identification: !!((vehicleData as any).no_identification || !(vehicleData as any).identification),
            chassis: vehicleData.chassis || '',
            purchase_price: vehicleData.purchase_price,
            sale_price_promo: vehicleData.sale_price_promo ?? 0,
            sale_price: vehicleData.sale_price ?? 0,
            purchase_date: vehicleData.purchase_date,
            notes: vehicleData.notes || '',
            internal_notes: (vehicleData as any).internal_notes || '',
            status: vehicleData.status,
            codigo_fipe: vehicleData.codigo_fipe,
            vehicleType: vehicleData.vehicleType,
            codigo_ano: vehicleData.codigo_ano,
            images: images,
            km: vehicleData.km || '',
            hours: (vehicleData as any).hours || '',
            zero_hours: !!((vehicleData as any).zero_hours || !(vehicleData as any).hours),
            cambio: vehicleData.cambio,
            motor: vehicleData.motor,
            ...consignmentData,
            showcase_top: !!(vehicleData as any).showcase_top,
            showcase_featured: !!(vehicleData as any).showcase_featured,
            showcase_week_highlight: !!(vehicleData as any).showcase_week_highlight,
            showcase_priority: Number((vehicleData as any).showcase_priority ?? 0) || 0,
          });

          setInitialFormData({
            brand_id: 1,
            model_id: 1,
            year_id: vehicleData.year.toString(),
            color: vehicleData.color,
            plate: vehicleData.plate || '',
            no_plate: !!(vehicleData.no_plate || !vehicleData.plate),
            identification: (vehicleData as any).identification || '',
            no_identification: !!((vehicleData as any).no_identification || !(vehicleData as any).identification),
            chassis: vehicleData.chassis || '',
            purchase_price: vehicleData.purchase_price,
            sale_price_promo: vehicleData.sale_price_promo ?? 0,
            sale_price: vehicleData.sale_price ?? 0,
            purchase_date: vehicleData.purchase_date,
            notes: vehicleData.notes || '',
            internal_notes: (vehicleData as any).internal_notes || '',
            status: vehicleData.status,
            codigo_fipe: vehicleData.codigo_fipe,
            vehicleType: vehicleData.vehicleType,
            codigo_ano: vehicleData.codigo_ano,
            images: images,
            km: vehicleData.km || '',
            hours: (vehicleData as any).hours || '',
            zero_hours: !!((vehicleData as any).zero_hours || !(vehicleData as any).hours),
            cambio: vehicleData.cambio,
            motor: vehicleData.motor,
            ...consignmentData,
            showcase_top: !!(vehicleData as any).showcase_top,
            showcase_featured: !!(vehicleData as any).showcase_featured,
            showcase_week_highlight: !!(vehicleData as any).showcase_week_highlight,
            showcase_priority: Number((vehicleData as any).showcase_priority ?? 0) || 0,
          });

          if (vehicleData.documents) setExistingDocs(vehicleData.documents);

          setLoading(false);
        } catch (error) {
          console.error('Erro ao carregar dados do veículo:', error);
          setError('Erro ao carregar dados do veículo.');
          setLoading(false);
        }
      } else {
        setVehicleCategory('car');
      }
    };

    loadVehicleData();
  }, [id, isEditing]);

  const loadModels = async (brandId: number) => {
    if (!brandId) return;

    setLoadingModels(true);
    try {
      const modelsData = await fipeService.getModels(brandId);
      setModels(modelsData);
      setLoadingModels(false);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
      setError('Erro ao carregar modelos de veículos.');
      setLoadingModels(false);
    }
  };

  const loadYears = async (brandId: number, modelId: number) => {
    if (!brandId || !modelId) return;

    setLoadingYears(true);
    try {
      const yearsData = await fipeService.getYears(brandId, modelId);
      setYears(yearsData);
      setLoadingYears(false);
    } catch (error) {
      console.error('Erro ao carregar anos:', error);
      setError('Erro ao carregar anos do modelo.');
      setLoadingYears(false);
    }
  };

  const loadFipePrice = async (brandId: number, modelId: number, yearId: string) => {
    if (!brandId || !modelId || !yearId) return;

    setFipePriceLoading(true);
    setFipePrice(null);
    try {
      const priceData = await fipeService.getPrice(brandId, modelId, yearId);
      setFipePrice(priceData.price);
      /* Não preenchemos preços automaticamente: compra e venda ficam com a loja; a FIPE é referência (botão copia para venda). */
      setFormData((prev) => ({
        ...prev,
        codigo_fipe: priceData.CodigoFipe ?? '',
        vehicleType: priceData.vehicleType ?? prev.vehicleType,
      }));
    } catch (error) {
      console.error('Erro ao carregar preço FIPE:', error);
      setError('Erro ao carregar preço FIPE do veículo.');
    } finally {
      setFipePriceLoading(false);
    }
  };

  const applyFipeSalePrice = () => {
    if (fipePrice == null || Number.isNaN(Number(fipePrice))) return;
    setFormData((prev) => ({ ...prev, sale_price: Number(fipePrice) }));
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name === 'plate' ? value.toUpperCase() : value });
  };

  const descriptionText = useMemo(
    () =>
      generateVehicleDescription(
        {
          brand: selectedBrand?.name,
          model: selectedModel?.name,
          year: selectedYear?.name || formData.year_id,
          color: formData.color,
          km: formData.km,
          cambio: formData.cambio,
          motor: formData.motor,
        },
        notesVariant
      ),
    [selectedBrand, selectedModel, selectedYear, formData.year_id, formData.color, formData.km, formData.cambio, formData.motor, notesVariant]
  );

  // Enquanto a descrição for automática, ela acompanha o que é preenchido no cadastro.
  useEffect(() => {
    if (autoNotes && descriptionText) {
      setFormData((prev) => (prev.notes === descriptionText ? prev : { ...prev, notes: descriptionText }));
    }
  }, [autoNotes, descriptionText]);

  const handleGenerateDescription = () => {
    if (!autoNotes && formData.notes.trim() && !window.confirm('Substituir a descrição atual pela descrição automática?')) {
      return;
    }
    if (autoNotes) setNotesVariant((v) => (v + 1) % VEHICLE_DESCRIPTION_VARIANTS);
    setAutoNotes(true);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    const onlyNumbers = value.replace(/[^0-9]/g, '')
    const numericValue = parseFloat(onlyNumbers || '0') / 100

    setFormData(prevState => ({
      ...prevState,
      [name]: numericValue,
    }))
  }

  const isConsignmentCommissionFixed = formData.consignment_commission_type === 'fixed';

  const formatKm = (value: string) => {
    const cleanedValue = value.replace(/\D/g, '');

    if (!cleanedValue) return '';

    return cleanedValue
      .replace(/(\d)(\d{3})$/, '$1.$2')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleNumberChangeKm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let numericValue = value.replace(/\D/g, '');
    const kmValue = parseFloat(numericValue) || 0;

    setFormData({ ...formData, [name]: kmValue });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });
  };


  const uploadImage = async (file: File) => {
    const fileToUpload = await prepareVehicleImageForUpload(file)
    const form = new FormData()
    form.append('arquivo', fileToUpload, fileToUpload.name)

    const response = await vehicleService.uploadFile(form)

    return response.url
  }
  // Handlers para os Autocomplete
  const handleBrandChange = (event: React.SyntheticEvent | null, newValue: Brand | null) => {
    setFipePrice(null);
    setSelectedBrand(newValue);
    setSelectedModel(null);
    setSelectedYear(null);
    setYears([]);

    if (newValue) {
      setFormData({
        ...formData,
        brand_id: newValue.id,
        model_id: 0,
        year_id: ''
      });
      loadModels(newValue.id);
    }
  };

  const handleModelChange = (event: React.SyntheticEvent | null, newValue: Model | null) => {
    setFipePrice(null);
    setSelectedModel(newValue);
    setSelectedYear(null);

    if (newValue && selectedBrand) {
      setFormData({
        ...formData,
        model_id: newValue.id,
        year_id: ''
      });
      loadYears(selectedBrand.id, newValue.id);
    }
  };

  const handleYearChange = (event: React.SyntheticEvent | null, newValue: Year | null) => {
    setSelectedYear(newValue);

    if (!newValue) {
      setFipePrice(null);
      setFipePriceLoading(false);
      setFormData((prev) => ({
        ...prev,
        year_id: '',
        codigo_fipe: '',
        vehicleType: '',
        codigo_ano: '',
      }));
      return;
    }

    if (selectedBrand && selectedModel) {
      setFormData((prev) => ({
        ...prev,
        year_id: newValue.id,
      }));

      loadFipePrice(selectedBrand.id, selectedModel.id, newValue.id);
    }
  };

  const handleVehicleCategoryChange = (e: SelectChangeEvent<string>) => {
    const next = e.target.value as VehicleCategory;
    setVehicleCategory(next);
    setModels([]);
    setYears([]);
    setFipePrice(null);
    setSelectedBrand(null);
    setSelectedModel(null);
    setSelectedYear(null);
    if (next === 'car') {
      setFormData((prev) => ({
        ...prev,
        brand_id: 0,
        model_id: 0,
        year_id: '',
        codigo_fipe: '',
        vehicleType: '',
        codigo_ano: '',
        no_plate: false,
        identification: '',
        hours: '',
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      brand_id: 0,
      model_id: 0,
      year_id: '',
      codigo_fipe: '',
      codigo_ano: '',
      vehicleType: next === 'motorcycle' ? 'Moto' : 'Outro',
      // Defaults para cadastro manual: outros veículos geralmente não tem placa
      no_plate: next === 'other' ? true : prev.no_plate,
      plate: next === 'other' ? '' : prev.plate,
      no_identification: next === 'other' ? true : prev.no_identification,
      identification: next === 'other' ? prev.identification : '',
      zero_hours: next === 'other' ? true : prev.zero_hours,
      hours: next === 'other' ? prev.hours : '',
    }));
  };

  const handleManualBrand = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setSelectedBrand({ id: 1, name });
    setFormData((prev) => ({ ...prev, brand_id: 1 }));
  };

  const handleManualModel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setSelectedModel({ id: 1, name });
    setFormData((prev) => ({ ...prev, model_id: 1 }));
  };

  const handleManualYear = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
    setFormData((prev) => ({ ...prev, year_id: raw, codigo_ano: raw }));
    setSelectedYear(raw ? { id: raw, name: raw } : null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações detalhadas
    const errors: string[] = [];

    if (useFipeTable) {
      if (!formData.brand_id || !formData.model_id || !formData.year_id) {
        errors.push('Por favor, preencha as informações de marca, modelo e ano do veículo.');
      }
    } else {
      if (!selectedBrand?.name?.trim() || !selectedModel?.name?.trim() || !formData.year_id?.trim()) {
        errors.push('Por favor, preencha as informações de marca, modelo e ano do veículo.');
      }
    }

    const isOther = vehicleCategory === 'other';
    const isNoPlate = !!formData.no_plate;
    if (isOther) {
      // Para outros veículos, usamos Identificação (não placa)
      if (!formData.no_identification && !formData.identification?.trim()) {
        errors.push('Por favor, informe a identificação do veículo.');
      }
    } else if (!isNoPlate) {
      if (!formData.plate) {
        errors.push('Por favor, informe a placa do veículo.');
      } else if (!/^[A-Z0-9]{7}$/.test(formData.plate.toUpperCase())) {
        errors.push('A placa do veículo deve conter 7 caracteres alfanuméricos.');
      }
    }

    if (!formData.color) {
      errors.push('Por favor, informe a cor do veículo.');
    }

    // Preço e data de compra são obrigatórios apenas quando não é consignado (a loja não compra o carro na consignação)
    if (!formData.is_consignado) {
      if (formData.purchase_price <= 0) {
        errors.push('O preço de compra deve ser maior que zero.');
      }
      if (!formData.purchase_date) {
        errors.push('Por favor, informe a data de compra do veículo.');
      } else {
        const purchaseDate = new Date(formData.purchase_date);
        const today = new Date();
        if (purchaseDate > today) {
          errors.push('A data de compra não pode ser futura.');
        }
      }
    }

    if (formData.is_consignado) {
      if ((formData.consignment_commission ?? 0) < 0) {
        errors.push('A comissão da consignação não pode ser negativa.');
      }
      if (formData.consignment_commission_type === 'percent' && formData.consignment_commission > 100) {
        errors.push('A comissão em porcentagem não pode ser maior que 100%.');
      }
      if ((formData.consignment_commission ?? 0) === 0) {
        if (!formData.consignment_zero_commission_reason?.trim()) {
          errors.push('Justifique o motivo da comissão 0 na consignação.');
        }
      }
    }
    const newDocs = documents
      .filter(doc => doc.url) // só os que já foram enviados e receberam URL
      .map(doc => ({
        type: doc.type,
        path: doc.url,
      }));

    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const form = new FormData();


      // Função para adicionar dados ao FormData apenas se não estiver vazio
      const appendField = (field: string, value: any) => {
        if (value !== undefined && value !== null && value !== '') {
          form.append(field, value);
        }
      };

      // Comparar e adicionar apenas os campos modificados
      if (isEditing) {
        // Para edição, comparamos com os dados iniciais (initialFormData)
        if (formData.brand_id !== initialFormData?.brand_id) appendField('brand', selectedBrand?.name || '');
        if (formData.model_id !== initialFormData?.model_id) appendField('model', selectedModel?.name || '');
        if (formData.year_id !== initialFormData?.year_id) appendField('year', parseInt(formData.year_id).toString());
        if (formData.color !== initialFormData?.color) appendField('color', formData.color.trim());
        if (formData.no_plate !== initialFormData?.no_plate) appendField('no_plate', formData.no_plate ? '1' : '0');
        if (vehicleCategory === 'other') {
          if (formData.no_identification !== initialFormData?.no_identification) appendField('no_identification', formData.no_identification ? '1' : '0');
          if (formData.zero_hours !== initialFormData?.zero_hours) appendField('zero_hours', formData.zero_hours ? '1' : '0');
          if (formData.identification !== initialFormData?.identification) appendField('identification', formData.identification.trim());
          if (formData.hours !== initialFormData?.hours) appendField('hours', formData.hours);
          // Para outros veículos, não forçar envio de placa
        } else {
          if (!formData.no_plate && formData.plate !== initialFormData?.plate) appendField('plate', formData.plate.toUpperCase());
        }
        if (formData.chassis !== initialFormData?.chassis) appendField('chassis', formData.chassis?.trim() || '');
        if (formData.purchase_price !== initialFormData?.purchase_price) appendField('purchase_price', formData.purchase_price.toString());
        if (formData.sale_price !== initialFormData?.sale_price) appendField('sale_price', formData.sale_price.toString());
        if (formData.sale_price_promo !== initialFormData?.sale_price_promo) appendField('sale_price_promo', formData.sale_price_promo.toString());

        if (formData.purchase_date !== initialFormData?.purchase_date) appendField('purchase_date', formData.purchase_date);
        if (formData.notes !== initialFormData?.notes) appendField('notes', formData.notes?.trim() || '');
        if (formData.internal_notes !== initialFormData?.internal_notes) appendField('internal_notes', formData.internal_notes?.trim() || '');
        if (formData.status !== initialFormData?.status) appendField('status', formData.status);
        if (formData.codigo_fipe !== initialFormData?.codigo_fipe) appendField('codigo_fipe', formData.codigo_fipe);
        if (formData.vehicleType !== initialFormData?.vehicleType) appendField('vehicleType', formData.vehicleType);
        if (formData.codigo_ano !== initialFormData?.codigo_ano) appendField('codigo_ano', formData.codigo_ano);
        if (vehicleCategory !== 'other') {
          if (formData.km !== initialFormData?.km) appendField('km', formData.km);
        }
        if (formData.cambio !== initialFormData?.cambio) appendField('cambio', formData.cambio);
        if (formData.motor !== initialFormData?.motor) appendField('motor', formData.motor);
        if (formData.is_consignado !== initialFormData?.is_consignado) appendField('is_consignado', formData.is_consignado ? '1' : '0');
        if (formData.is_consignado) {
          if (formData.consignment_commission_type !== initialFormData?.consignment_commission_type) {
            appendField('consignment_commission_type', toApiConsignmentCommissionType(formData.consignment_commission_type));
          }
          if (formData.consignment_commission !== initialFormData?.consignment_commission) appendField('consignment_commission', formData.consignment_commission.toString());
          if (formData.consignment_exposure !== initialFormData?.consignment_exposure) appendField('consignment_exposure', formData.consignment_exposure);
          if (formData.consignment_owner_name !== initialFormData?.consignment_owner_name) appendField('consignment_owner_name', formData.consignment_owner_name);
          if (formData.consignment_owner_contact !== initialFormData?.consignment_owner_contact) appendField('consignment_owner_contact', formData.consignment_owner_contact);
          if (formData.consignment_contract_date !== initialFormData?.consignment_contract_date) appendField('consignment_contract_date', formData.consignment_contract_date);
          if (formData.consignment_notes !== initialFormData?.consignment_notes) appendField('consignment_notes', formData.consignment_notes);
          if (formData.consignment_zero_commission_reason !== initialFormData?.consignment_zero_commission_reason) {
            appendField('consignment_zero_commission_reason', formData.consignment_zero_commission_reason);
          }
        }

        // Vitrine (controle)
        if (formData.showcase_top !== initialFormData?.showcase_top) appendField('showcase_top', formData.showcase_top ? '1' : '0');
        if (formData.showcase_featured !== initialFormData?.showcase_featured) appendField('showcase_featured', formData.showcase_featured ? '1' : '0');
        if (formData.showcase_week_highlight !== initialFormData?.showcase_week_highlight) appendField('showcase_week_highlight', formData.showcase_week_highlight ? '1' : '0');
        if (formData.showcase_priority !== initialFormData?.showcase_priority) appendField('showcase_priority', String(formData.showcase_priority ?? 0));

        // Para imagens, só envia as novas imagens, ou as alteradas
        if (formData.images?.length > 0) {
          const newImages = formData.images.filter((img: any) => img.isNew)

          newImages.forEach((image: any) => {
            form.append('images[]', image.url)
          })

          if (imageOrderKey(formData.images) !== imageOrderKey(initialFormData?.images ?? [])) {
            formData.images.forEach((image: any) => {
              form.append('image_order[]', image.url)
            })
          }
        }


      } else {
        // Para criação, envia todos os dados
        appendField('brand', selectedBrand?.name || '');
        appendField('model', selectedModel?.name || '');
        appendField('year', parseInt(formData.year_id).toString());;
        appendField('color', formData.color.trim());
        appendField('no_plate', formData.no_plate ? '1' : '0');
        if (vehicleCategory === 'other') {
          appendField('no_identification', formData.no_identification ? '1' : '0');
          appendField('zero_hours', formData.zero_hours ? '1' : '0');
          appendField('identification', formData.identification.trim());
        } else if (!formData.no_plate) {
          appendField('plate', formData.plate.toUpperCase());
        }
        appendField('chassis', formData.chassis?.trim() || '');
        appendField('notes', formData.notes?.trim() || '');
        appendField('internal_notes', formData.internal_notes?.trim() || '');
        appendField('status', formData.status);
        appendField('codigo_fipe', formData.codigo_fipe);
        appendField('vehicleType', formData.vehicleType);
        appendField('codigo_ano', formData.year_id);
        if (vehicleCategory === 'other') {
          appendField('hours', formData.hours);
        } else {
          appendField('km', formData.km);
        }
        appendField('cambio', formData.cambio);
        appendField('motor', formData.motor);
        appendField('is_consignado', formData.is_consignado ? '1' : '0');
        if (formData.is_consignado) {
          appendField('consignment_commission_type', toApiConsignmentCommissionType(formData.consignment_commission_type));
          appendField('consignment_commission', formData.consignment_commission.toString());
          if (formData.consignment_exposure) appendField('consignment_exposure', formData.consignment_exposure);
          appendField('consignment_owner_name', formData.consignment_owner_name);
          appendField('consignment_owner_contact', formData.consignment_owner_contact);
          appendField('consignment_contract_date', formData.consignment_contract_date);
          appendField('consignment_notes', formData.consignment_notes);
          appendField('consignment_zero_commission_reason', formData.consignment_zero_commission_reason);
        }

        // Vitrine (controle)
        appendField('showcase_top', formData.showcase_top ? '1' : '0');
        appendField('showcase_featured', formData.showcase_featured ? '1' : '0');
        appendField('showcase_week_highlight', formData.showcase_week_highlight ? '1' : '0');
        appendField('showcase_priority', String(formData.showcase_priority ?? 0));

        // Adiciona imagens para criação
        if (formData.images?.length > 0) {
          if (formData.images?.length > 0) {
            formData.images.forEach((image: any) => {
              form.append('images[]', image.url)
            })
          }
        }
      }
      appendField('sale_price', formData?.sale_price?.toString()  ?? 0)
      appendField('sale_price_promo', formData?.sale_price_promo?.toString() ?? 0)
      if (newDocs.length > 0) {
        form.append('documents', JSON.stringify(newDocs));
      }
      if (!formData.is_consignado) {
        appendField('purchase_price', formData?.purchase_price.toString())
        appendField('purchase_date', formData.purchase_date)
      }
      if (isEditing) {
        // Para edição também usamos o FormData

        await vehicleService.updateVehicle(parseInt(id!), form);

      } else {
        await vehicleService.createVehicle(form);
      }

      navigate(`/veiculos${(location.state as { listSearch?: string } | null)?.listSearch ?? ''}`);
    } catch (error: any) {
      console.error('Erro ao salvar veículo:', error);
      let errorMessage = 'Ocorreu um erro ao salvar os dados do veículo.';

      // Tentar extrair mensagem mais específica do erro
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setSaving(false);
    }
  };

  const addImageFiles = async (selectedFiles: File[]) => {
    if (selectedFiles.length === 0) return

    if (formData.images.length + selectedFiles.length > MAX_VEHICLE_IMAGES) {
      showUploadError(`Você pode adicionar no máximo ${MAX_VEHICLE_IMAGES} imagens.`)
      return
    }

    setUploading(true)
    setUploadProgress({ done: 0, total: selectedFiles.length })

    try {
      for (let index = 0; index < selectedFiles.length; index++) {
        try {
          const url = await uploadImage(selectedFiles[index])
          setFormData((prev) => ({
            ...prev,
            images: [...prev.images, { url, isNew: true }],
          }))
          setUploadProgress({ done: index + 1, total: selectedFiles.length })
        } catch (err) {
          console.error('Erro ao enviar imagem:', err)
          showUploadError(formatImageUploadError(err))
          break
        }
      }
    } finally {
      setUploading(false)
    }
  }

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target
    const files = Array.from(input.files ?? [])
    await addImageFiles(files)
    input.value = ''
  }

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDraggingFiles(false)
    if (saving || uploading) return
    const files = Array.from(e.dataTransfer.files)
    const images = files.filter(isImageFile)
    if (images.length < files.length) showUploadError('Somente arquivos de imagem foram adicionados.')
    void addImageFiles(images)
  }

  const handleReorderImages = (images: any[]) => {
    const currentImageKey = formData.images[currentIndex]?.url
    setFormData((prev) => ({ ...prev, images }))
    setCurrentIndex(Math.max(0, images.findIndex((image) => image.url === currentImageKey)))
  }

  const handleRemoveImage = async (index: number, image: any) => {
    // Foto salva só sai do banco depois de confirmar; foto nova ainda nem foi gravada no veículo.
    if (image.id) {
      if (!window.confirm('Apagar esta foto do veículo? Isso não pode ser desfeito.')) return;

      const result = await vehicleService.deleteImageVehicle(parseInt(id!), image.id);
      if (result.status !== 204) return;
    }

    const images = [...formData.images];
    images.splice(index, 1);
    setFormData((prev) => ({ ...prev, images }));
    setInitialFormData((prev) =>
      prev ? { ...prev, images: prev.images.filter((item: any) => item.id !== image.id || !image.id) } : prev
    );
    setCurrentIndex((current) => Math.max(0, Math.min(current, images.length - 1)));
    return;

  };


  const handleBack = () => {
    navigate(`/veiculos${(location.state as { listSearch?: string } | null)?.listSearch ?? ''}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 5, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Carregando dados do veículo...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <VehicleChecklistDialog open={checklistDialogOpen} onClose={() => setChecklistDialogOpen(false)} meta={checklistMeta} />
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" component="h1">
          {isEditing ? 'Editar Veículo' : 'Novo Veículo'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ChecklistIcon />}
            disabled={saving}
            onClick={() => setChecklistDialogOpen(true)}
          >
            Checklist / Vistoria
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={saving}
          >
            Voltar
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, position: 'relative' }}>
        {saving && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Salvando dados do veículo...
            </Typography>
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={isDisabled}>
                <InputLabel id="vehicle-category-label">Tipo de veículo</InputLabel>
                <Select
                  labelId="vehicle-category-label"
                  id="vehicle-category"
                  label="Tipo de veículo"
                  value={vehicleCategory ?? 'car'}
                  onChange={handleVehicleCategoryChange}
                >
                  <MenuItem value="car">Carro (tabela FIPE)</MenuItem>
                  <MenuItem value="motorcycle">Moto (cadastro manual)</MenuItem>
                  <MenuItem value="other">Outro veículo (cadastro manual)</MenuItem>
                </Select>
                <FormHelperText>
                  {useFipeTable
                    ? 'Marca, modelo e ano são preenchidos pela tabela FIPE.'
                    : 'Sem consulta FIPE: informe marca, modelo e ano manualmente.'}
                </FormHelperText>
              </FormControl>
            </Grid>

            {useFipeTable ? (
              <>
                {/* Seleção de Marca com Autocomplete */}
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    id="brand-select"
                    options={brands}
                    getOptionLabel={(option) => option.name}
                    value={selectedBrand}
                    onChange={handleBrandChange}
                    loading={loadingBrands}
                    disabled={isDisabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Marca"
                        required
                        fullWidth
                        error={!selectedBrand && error?.includes('marca')}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingBrands ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Seleção de Modelo com Autocomplete */}
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    id="model-select"
                    options={models}
                    getOptionLabel={(option) => option.name}
                    value={selectedModel}
                    onChange={handleModelChange}
                    loading={loadingModels}
                    disabled={!selectedBrand || isDisabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Modelo"
                        required
                        fullWidth
                        error={!selectedModel && error?.includes('modelo')}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingModels ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Seleção de Ano com Autocomplete */}
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    id="year-select"
                    options={years}
                    getOptionLabel={(option) => option.name?.toString()}  // Ensure it returns a string
                    value={selectedYear}
                    onChange={handleYearChange}
                    loading={loadingYears}
                    disabled={!selectedModel || isDisabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ano"
                        required
                        fullWidth
                        error={!selectedYear && error?.includes('ano')}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingYears ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Marca"
                    required
                    fullWidth
                    value={selectedBrand?.name || ''}
                    onChange={handleManualBrand}
                    disabled={isDisabled}
                    error={error?.includes('marca') || error?.includes('modelo') || error?.includes('ano')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Modelo"
                    required
                    fullWidth
                    value={selectedModel?.name || ''}
                    onChange={handleManualModel}
                    disabled={isDisabled}
                    error={error?.includes('marca') || error?.includes('modelo') || error?.includes('ano')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Ano"
                    required
                    fullWidth
                    value={formData.year_id}
                    onChange={handleManualYear}
                    disabled={isDisabled}
                    placeholder="Ex.: 2022"
                    inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                    error={error?.includes('marca') || error?.includes('modelo') || error?.includes('ano')}
                  />
                </Grid>
              </>
            )}

            {useFipeTable && selectedBrand && selectedModel && selectedYear ? (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={(theme) => ({
                    p: 2,
                    borderRadius: 2,
                    bgcolor: theme.palette.mode === 'light' ? 'rgba(21,101,192,0.06)' : 'action.hover',
                    borderColor:
                      theme.palette.mode === 'light' ? 'rgba(21,101,192,0.28)' : theme.palette.divider,
                  })}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: 'primary.dark' }}>
                    Sugestão de preço (FIPE)
                  </Typography>
                  {fipePriceLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CircularProgress size={22} thickness={4} />
                      <Typography variant="body2" color="text.secondary">
                        Consultando valor médio na tabela FIPE…
                      </Typography>
                    </Box>
                  ) : fipePrice != null ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: 2,
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                          Referência FIPE de mercado para vitrine; ajuste conforme a estratégia da loja (a compra não segue a
                          FIPE).
                        </Typography>
                        <Typography variant="h6" component="p" sx={{ fontWeight: 800, mt: 0.75 }}>
                          {formatCurrency(fipePrice)}
                        </Typography>
                        {formData.codigo_fipe?.trim() ? (
                          <Typography variant="caption" color="text.secondary">
                            Código FIPE gravado no cadastro: {formData.codigo_fipe.trim()}
                          </Typography>
                        ) : null}
                      </Box>
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={applyFipeSalePrice}
                        sx={{ flexShrink: 0, mt: { xs: 0, sm: 0.5 }, whiteSpace: 'nowrap' }}
                      >
                        Copiar para preço de venda
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Não foi possível obter este valor FIPE para a combinação selecionada. Verifique marca, modelo e ano ou
                      tente novamente.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ) : null}

            {/* Restante do formulário */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={COMMON_COLORS}
                value={formData.color}
                inputValue={formData.color}
                onInputChange={(_, value) => setFormData((prev) => ({ ...prev, color: value }))}
                renderInput={(params) => (
                  <TextField {...params} label="Cor" name="color" required error={error?.includes('cor')} />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {vehicleCategory === 'other' ? (
                <>
                  <TextField
                    label="Identificação"
                    name="identification"
                    value={formData.identification}
                    onChange={handleInputChange}
                    fullWidth
                    required={!formData.no_identification}
                    disabled={formData.no_identification}
                    error={error?.includes('identificação')}
                    helperText={
                      formData.no_identification
                        ? 'Zero horas: identificação pode ser cadastrada depois.'
                        : (error?.includes('identificação') ? 'Informe a identificação do veículo' : '')
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.no_identification}
                        onChange={(_, v) =>
                          setFormData((p) => ({
                            ...p,
                            no_identification: v,
                            identification: v ? '' : p.identification,
                            zero_hours: v ? true : p.zero_hours,
                            hours: v ? '' : p.hours,
                          }))
                        }
                      />
                    }
                    label="0 horas (sem identificação)"
                  />
                </>
              ) : (
                <>
                  <TextField
                    label="Placa"
                    name="plate"
                    value={formData.plate}
                    onChange={handleInputChange}
                    fullWidth
                    required={!formData.no_plate}
                    disabled={formData.no_plate}
                    error={error?.includes('placa')}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    helperText={
                      formData.no_plate
                        ? 'Veículo 0 km: placa pode ser cadastrada depois.'
                        : (error?.includes('placa') ? 'Formato inválido de placa' : '')
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.no_plate}
                        onChange={(_, v) => setFormData((p) => ({ ...p, no_plate: v, plate: v ? '' : p.plate }))}
                      />
                    }
                    label="0 km (sem placa)"
                  />
                </>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Chassi"
                name="chassis"
                value={formData.chassis}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            {/* Consignação */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_consignado}
                      onChange={(e) => setFormData({
                        ...formData,
                        is_consignado: e.target.checked,
                        purchase_price: e.target.checked ? 0 : formData.purchase_price,
                        purchase_date: e.target.checked ? '' : formData.purchase_date,
                        // Ao desmarcar consignação, limpar campos para não disparar validações/back-end
                        consignment_owner_name: e.target.checked ? formData.consignment_owner_name : '',
                        consignment_owner_contact: e.target.checked ? formData.consignment_owner_contact : '',
                        consignment_contract_date: e.target.checked ? formData.consignment_contract_date : '',
                        consignment_exposure: e.target.checked ? formData.consignment_exposure : '',
                        consignment_notes: e.target.checked ? formData.consignment_notes : '',
                        consignment_zero_commission_reason: e.target.checked ? formData.consignment_zero_commission_reason : '',
                      })}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ConsignmentIcon fontSize="small" />
                      <Typography variant="body1">Veículo consignado</Typography>
                    </Box>
                  }
                />
                <FormHelperText sx={{ ml: 0, mt: 0.5 }}>
                  O proprietário deixa o veículo na loja para que encontremos um comprador. A propriedade só é transferida após a venda; a loja cobra comissão (geralmente 5% a 7%).
                </FormHelperText>
                <Collapse in={formData.is_consignado} unmountOnExit>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Tipo de comissão"
                        name="consignment_commission_type"
                        select
                        value={formData.consignment_commission_type}
                        onChange={(e) => setFormData({ ...formData, consignment_commission_type: e.target.value as ConsignmentCommissionType, consignment_commission: 0 })}
                        fullWidth
                        helperText="Escolha se a comissão é percentual sobre a venda ou um valor fixo em reais."
                      >
                        {(Object.entries(CONSIGNMENT_COMMISSION_TYPE_LABELS) as [ConsignmentCommissionType, string][]).map(([value, label]) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={isConsignmentCommissionFixed ? 'Comissão (R$)' : 'Comissão (%)'}
                        name="consignment_commission"
                        type={isConsignmentCommissionFixed ? 'text' : 'number'}
                        value={
                          isConsignmentCommissionFixed
                            ? formatCurrency(formData.consignment_commission || 0)
                            : (formData.consignment_commission ?? '')
                        }
                        onChange={(e) => {
                          if (isConsignmentCommissionFixed) {
                            handleNumberChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
                            return;
                          }
                          setFormData({ ...formData, consignment_commission: parseFloat(e.target.value) || 0 })
                        }}
                        fullWidth
                        inputProps={isConsignmentCommissionFixed ? undefined : { min: 0, max: 100, step: 0.5 }}
                        helperText={
                          (formData.consignment_commission ?? 0) === 0
                            ? 'Comissão 0 exige justificativa.'
                            : (isConsignmentCommissionFixed ? 'Ex.: R$ 1.500,00' : 'Ex.: 5 a 7')
                        }
                      />
                    </Grid>
                    {(formData.consignment_commission ?? 0) === 0 && (
                      <Grid item xs={12}>
                        <TextField
                          label="Justificativa (comissão 0)"
                          name="consignment_zero_commission_reason"
                          value={formData.consignment_zero_commission_reason}
                          onChange={handleInputChange}
                          fullWidth
                          required={!!formData.is_consignado && (formData.consignment_commission ?? 0) === 0}
                          multiline
                          rows={2}
                          placeholder="Explique por que este consignado não terá lucro/comissão para a loja."
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel id="consignment_exposure-label">Exposição</InputLabel>
                        <Select
                          labelId="consignment_exposure-label"
                          name="consignment_exposure"
                          value={formData.consignment_exposure}
                          onChange={handleSelectChange}
                          label="Exposição"
                        >
                          <MenuItem value="">Selecione</MenuItem>
                          <MenuItem value="store">{CONSIGNMENT_EXPOSURE_LABELS.store}</MenuItem>
                          <MenuItem value="owner">{CONSIGNMENT_EXPOSURE_LABELS.owner}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Data do contrato"
                        name="consignment_contract_date"
                        type="date"
                        value={formData.consignment_contract_date || ''}
                        onChange={handleInputChange}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Nome do proprietário"
                        name="consignment_owner_name"
                        value={formData.consignment_owner_name}
                        onChange={handleInputChange}
                        fullWidth
                        required={!!formData.is_consignado}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Contato do proprietário"
                        name="consignment_owner_contact"
                        value={formData.consignment_owner_contact}
                        onChange={handleInputChange}
                        fullWidth
                        placeholder="Telefone ou e-mail"
                        required={!!formData.is_consignado}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Observações da consignação"
                        name="consignment_notes"
                        value={formData.consignment_notes}
                        onChange={handleInputChange}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Prazos, responsabilidades, laudo cautelar..."
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Preço de Compra"
                name="purchase_price"
                type="text"
                value={formatCurrency(formData.purchase_price || 0)}
                onChange={handleNumberChange}
                fullWidth
                required={!formData.is_consignado}
                error={error?.includes('preço')}
                helperText={
                  formData.is_consignado
                    ? 'Opcional para consignados (a loja não compra o veículo)'
                    : useFipeTable
                      ? 'Valor que a loja pagou no negócio — independente da FIPE (sugestão FIPE é para venda, acima).'
                      : ''
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Preço de venda"
                name="sale_price"
                type="text"
                value={formatCurrency(formData.sale_price || 0)}
                onChange={handleNumberChange}
                fullWidth
                required={!formData.is_consignado}
                error={error?.includes('preço')}
                helperText={
                  formData.is_consignado
                    ? 'Opcional para consignados (a loja não compra o veículo)'
                    : useFipeTable
                      ? 'Preço de vitrine. Você pode usar o botão na sugestão FIPE para copiar a referência.'
                      : ''
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Preço promocional de venda"
                name="sale_price_promo"
                type="text"
                value={formatCurrency(formData.sale_price_promo || 0)}
                onChange={handleNumberChange}
                fullWidth
                required={!formData.is_consignado}
                error={error?.includes('preço')}
                helperText={
                  formData.is_consignado
                    ? 'Opcional para consignados (a loja não compra o veículo)'
                    : 'Quando usar preço à vista diferente.'
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Data de Compra"
                name="purchase_date"
                type="date"
                value={formData.purchase_date ? new Date(formData.purchase_date).toISOString().split('T')[0] : ''}
                onChange={handleInputChange}
                fullWidth
                required={!formData.is_consignado}
                error={error?.includes('data')}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={
                  formData.is_consignado
                    ? 'Opcional para consignados'
                    : (error?.includes('data') ? 'Data inválida' : '')
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                component="label"
                variant="outlined"
                sx={{
                  mb: 2,
                  borderRadius: '8px',
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                  borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                    borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.15)',
                  }
                }}
                disabled={saving || uploading}
                startIcon={uploading ? <CircularProgress size={20} /> : <FileUploadIcon />}
              >
                {uploading ? `Enviando ${uploadProgress.done + 1} de ${uploadProgress.total}...` : 'Adicionar Imagens'}
                <input
                  type="file"
                  name="images"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                  onChange={handleImageChange}
                  multiple
                  disabled={saving || uploading}
                  style={{ display: 'none' }}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Selecione várias de uma vez ou arraste as fotos para a área abaixo. Fotos grandes ou HEIC são otimizadas antes do envio.
              </Typography>


            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleSelectChange}
                  label="Status"
                >
                  <MenuItem value="available">Disponível</MenuItem>
                  <MenuItem value="sold">Vendido</MenuItem>
                  <MenuItem value="maintenance">Em Manutenção</MenuItem>
                </Select>
              </FormControl>
            </Grid>


            <Grid
              container
              spacing={2}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingLeft: 10
              }}
            >
              {/* IMAGEM GRANDE */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    position: "relative",
                    width: "95%",
                    height: 350,
                    display: "flex",
                    justifyContent: 'center',
                    borderRadius: 2,
                    overflow: "hidden",
                    background: "#eee"
                  }}
                >
                  {currentImage && (
                    <img
                      src={currentImage.url || URL.createObjectURL(currentImage)}
                      alt="preview"

                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                    />
                  )}

                  {/* SETA ESQUERDA */}
                  <IconButton
                    onClick={prevImage}
                    disabled={currentIndex === 0}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 10,
                      transform: "translateY(-50%)",
                      background: "rgba(0,0,0,0.5)",
                      color: "#fff"
                    }}
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>

                  {/* SETA DIREITA */}
                  <IconButton
                    onClick={nextImage}
                    disabled={currentIndex === formData.images.length - 1}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 10,
                      transform: "translateY(-50%)",
                      background: "rgba(0,0,0,0.5)",
                      color: "#fff"
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>

                  {/* CONTADOR */}
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 10,
                      right: 10,
                      background: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      fontSize: 14
                    }}
                  >
                    {currentIndex + 1} / {formData.images.length}
                  </Typography>
                </Box>
              </Grid>

              {/* MINIATURAS */}
              <Grid
                item
                xs={12}
                onDragOver={(e) => {
                  if (!e.dataTransfer.types.includes('Files')) return;
                  e.preventDefault();
                  setDraggingFiles(true);
                }}
                onDragLeave={() => setDraggingFiles(false)}
                onDrop={handleImageDrop}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: draggingFiles ? 'primary.main' : 'divider',
                    bgcolor: draggingFiles ? 'action.hover' : 'transparent',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: formData.images.length ? 1.5 : 0 }}>
                    {formData.images.length > 0
                      ? 'Arraste as fotos para definir a ordem. A primeira é a capa na vitrine e na OLX. No celular, segure a foto antes de arrastar.'
                      : 'Solte as fotos aqui ou use o botão "Adicionar Imagens".'}
                  </Typography>
                  <VehicleImageSorter
                    images={formData.images}
                    currentIndex={currentIndex}
                    disabled={saving}
                    onSelect={setCurrentIndex}
                    onRemove={handleRemoveImage}
                    onReorder={handleReorderImages}
                  />
                </Box>
              </Grid>

            </Grid>
            <Grid item xs={12} md={4}>
              {vehicleCategory === 'other' ? (
                <>
                  <TextField
                    label="Horas"
                    name="hours"
                    type="text"
                    value={formData.hours}
                    onChange={handleInputChange}
                    fullWidth
                    disabled={formData.zero_hours}
                    helperText={
                      formData.zero_hours
                        ? '0 horas: campo vem desabilitado e pode ser preenchido depois.'
                        : 'Informe as horas de uso.'
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.zero_hours}
                        onChange={(_, v) =>
                          setFormData((p) => ({
                            ...p,
                            zero_hours: v,
                            hours: v ? '' : p.hours,
                          }))
                        }
                      />
                    }
                    label="0 horas (desabilitar campo)"
                  />
                </>
              ) : (
                <TextField
                  label="Quilometragem"
                  name="km"
                  type="text"
                  value={formData.km ? formatKm(formData.km.toString()) : ''}
                  onChange={handleNumberChangeKm}
                  fullWidth
                  error={error?.includes('km')}
                  helperText={error?.includes('km') ? 'Valor inválido para km' : ''}
                />
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth >
                <InputLabel id="cambio-label">Tipo de Câmbio</InputLabel>
                <Select
                  labelId="cambio-label"
                  id="cambio"
                  name="cambio"
                  value={formData.cambio}
                  onChange={handleSelectChange}
                  label="Tipo de Câmbio"
                  error={error?.includes('cambio')}
                >
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="automatico">Automático</MenuItem>
                  <MenuItem value="seletor">CVT</MenuItem>
                  {/* Adicione outros tipos de câmbio, se necessário */}
                </Select>

              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Motor"
                name="motor"
                value={formData.motor}
                onChange={handleInputChange}
                fullWidth
                error={error?.includes('motor')}
              />
            </Grid>



            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                disabled={loadingDoc} // desativa enquanto carrega
              >
                {loadingDoc ? (
                  <>
                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                    Enviando...
                  </>
                ) : (
                  'Anexar documentos do veículo'
                )}
                <input hidden type="file" onChange={handleAddDocument} />
              </Button>
            </Grid>

            <Grid item xs={12}>
              {existingDocs.map(d => (
                <Box key={d.id} display="flex" gap={1}>
                  <a href={d.path} target="_blank" rel="noreferrer">{d.type}</a>
                  <Button color="error" onClick={() => removeExistingDoc(d.id)}>Remover</Button>
                </Box>
              ))}
              {documents.map((d, i) => (
                <Box key={i} display="flex" gap={1}>
                  {d.type} - {d.file.name}
                  <Button color="error" onClick={() => handleRemoveDocument(i)}>Remover</Button>
                </Box>
              ))}
            </Grid>


            <Grid item xs={12}>
              <TextField
                label="Descrição pública (aparece na vitrine)"
                name="notes"
                value={formData.notes}
                onChange={(e) => {
                  setAutoNotes(false);
                  handleInputChange(e as React.ChangeEvent<HTMLInputElement>);
                }}
                fullWidth
                multiline
                minRows={6}
                helperText={
                  autoNotes
                    ? 'Preenchida automaticamente com os dados do cadastro. Pode editar à vontade.'
                    : 'Não cite dados internos: este texto é visto pelos clientes.'
                }
              />
              <Button
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleGenerateDescription}
                disabled={!selectedBrand || !selectedModel}
                sx={{ mt: 1 }}
              >
                {autoNotes ? 'Gerar outro texto' : 'Gerar descrição automática'}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Observações internas (somente equipe)"
                name="internal_notes"
                value={formData.internal_notes}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
              />
            </Grid>

            {/* Vitrine pública */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Vitrine pública
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Controle quais veículos terão destaque na vitrine.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showcase_top}
                    onChange={(_, v) => setFormData((p) => ({ ...p, showcase_top: v }))}
                  />
                }
                label="Aparecer no topo"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showcase_featured}
                    onChange={(_, v) => setFormData((p) => ({ ...p, showcase_featured: v }))}
                  />
                }
                label="Carro em destaque"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showcase_week_highlight}
                    onChange={(_, v) => setFormData((p) => ({ ...p, showcase_week_highlight: v }))}
                  />
                }
                label="Destaque da semana"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Prioridade de destaque"
                type="number"
                value={formData.showcase_priority}
                onChange={(e) => setFormData((p) => ({ ...p, showcase_priority: Number(e.target.value || 0) }))}
                fullWidth
                helperText="Maior aparece primeiro (0 = sem prioridade)"
                inputProps={{ step: 1 }}
              />
            </Grid>


            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </Alert>
              )}

              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={isDisabled}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={isDisabled ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={isDisabled}
              >
                {isDisabled ? 'Salvando...' : 'Salvar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar
        open={uploadSnackbar.open}
        autoHideDuration={9000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          closeUploadSnackbar();
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Fade}
        sx={{
          // classes do MUI (ex.: anchorOriginTopCenter) fixam top/bottom; !important centraliza de verdade
          position: 'fixed',
          top: '50% !important',
          left: '50% !important',
          right: 'auto !important',
          bottom: 'auto !important',
          transform: 'translate(-50%, -50%) !important',
          justifyContent: 'center',
          zIndex: (theme) => (theme.zIndex.modal ?? 1300) + 20,
        }}
      >
        <Paper
          elevation={0}
          role="alert"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            py: 1.5,
            px: 2,
            borderRadius: 2,
            minWidth: { xs: 'min(100%, 100vw - 32px)', sm: 400 },
            maxWidth: 520,
            bgcolor: 'error.main',
            color: 'error.contrastText',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0,0,0,0.45)'
                : '0 10px 40px rgba(0,0,0,0.18)',
          }}
        >
          <Typography
            component="p"
            variant="body2"
            sx={{ flex: 1, fontWeight: 600, lineHeight: 1.5, pt: 0.25, pr: 0.5 }}
          >
            {uploadSnackbar.message}
          </Typography>
          <IconButton
            size="small"
            onClick={closeUploadSnackbar}
            sx={{ color: 'inherit', mt: -0.25, mr: -0.5 }}
            aria-label="Fechar aviso"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Snackbar>
    </Box>
  );
};

export default VehicleForm; 